import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
    useTheme,
    Box,
    Typography,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Card,
    CardContent,
    CardActions,
    Collapse,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    MoreVert,
    ExpandMore,
    CheckCircle,
    Cancel,
    HourglassEmpty,
    CalendarMonth,
} from '@mui/icons-material';
import api from 'state/axios';
import DeleteConfirm from 'components/DeleteConfirm';


const STATUS_CONFIG = {
    Approved: { color: 'success.main', chipColor: 'success', icon: <CheckCircle fontSize="small" /> },
    Rejected: { color: 'error.main',   chipColor: 'error',   icon: <Cancel fontSize="small" /> },
    Pending:  { color: 'warning.main', chipColor: 'warning', icon: <HourglassEmpty fontSize="small" /> },
};


const RequestCard = ({ request, onDeleteSuccess }) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const statusKey = request?.status?.status || 'Pending';
    const statusCfg = STATUS_CONFIG[statusKey] ?? STATUS_CONFIG.Pending;
    const isPending = statusKey === 'Pending';

    const borderColor = theme.palette[statusCfg.chipColor]?.main ?? theme.palette.warning.main;
    const bandBg = borderColor + '22';

    const [commentsExpanded, setCommentsExpanded] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null);
    const [idToDelete, setIdToDelete] = useState(null);

    const handleMoreClick = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleDeleteClick = (id) => {
        setIdToDelete(id);
        setDeleteType('single');
        setConfirmDialogOpen(true);
        handleClose();
    };

    const executeDelete = async () => {
        try {
            if (!idToDelete) return;
            await api.patch(`/crewrequests/soft-delete/${idToDelete}`, {});
            toast.success('Request deleted successfully.');
            onDeleteSuccess?.();
        } catch (error) {
            toast.error(error?.message || 'Failed to delete request!');
        }
    };

    const handlerName = request.status?.handler
        ? `${request.status.handler.lastName?.toUpperCase() ?? ''} ${request.status.handler.firstName?.toUpperCase() ?? ''}`.trim()
        : null;

    const comments = Array.isArray(request.comments) ? request.comments : [];

    return (
        <Card
            variant="outlined"
            sx={{ borderLeft: `2px solid ${borderColor}` }}
        >
            {/* ── HEADER BAND ── */}
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                px={2}
                py={1}
                sx={{ backgroundColor: bandBg }}
            >
                {request.requestType && (
                    <Chip
                        label={request.requestType}
                        size="small"
                        sx={{ 
                            bgcolor: borderColor, 
                            color: 'text.primary', 
                        }}
                    />
                )}
                <Typography 
                    variant="h6" 
                    sx={{ flex: 1, fontWeight: 600 }}
                >
                    {request.title}
                </Typography>
                <Chip
                    size="small"
                    icon={statusCfg.icon}
                    label={statusKey}
                    color={statusCfg.chipColor}
                />
                {isPending && (
                    <>
                        <IconButton size="small" onClick={handleMoreClick}>
                            <MoreVert fontSize="small" />
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                            <MenuItem onClick={() => { navigate(`/crewrequests/${request._id}`); handleClose(); }}>
                                Edit
                            </MenuItem>
                            <MenuItem onClick={() => handleDeleteClick(request._id)}>
                                Delete
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>

            {/* ── INFO ROW ── */}
            <Box px={2} py={1}>
                {/* Line 1: name */}
                <Typography variant="body2" fontWeight={600}>
                {request.crewCode
                    ? `${request.crewCode} - ${request.crewName?.toUpperCase()}`
                    : request.crewName?.toUpperCase() || '-'}
                </Typography>
                {/* Line 2: date range + createdAt */}
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarMonth sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                            {format(new Date(request.startDate), 'dd/MM/yyyy')} - {format(new Date(request.endDate), 'dd/MM/yyyy')}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Created at: {format(new Date(request.createdAt), 'HH:mm dd/MM/yy')}
                    </Typography>
                </Box>
                {/* Line 4: handler */}
                {handlerName && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        Handled by <strong>{handlerName}</strong>
                        {request.status?.statusDate && (
                            <> at {format(new Date(request.status.statusDate), 'HH:mm dd/MM/yy')}</>
                        )}
                    </Typography>
                )}
            </Box>

            {/* ── CONTENT ── */}
            <CardContent>
                <Box
                    sx={{ fontSize: '0.8rem', lineHeight: 1.5, '& p': { my: 0.5 } }}
                    dangerouslySetInnerHTML={{ __html: request.content }}
                />
            </CardContent>

            {/* ── COMMENTS (Collapse) ── */}
            <Collapse in={commentsExpanded} timeout="auto" unmountOnExit>
                <CardContent>
                    <Divider sx={{ mb: 1 }} />
                    <Typography 
                        variant="subtitle2" 
                        color="text.secondary" 
                        fontWeight={600} 
                        display="block" 
                        mb={1}
                    >
                        Comments ({comments.length})
                    </Typography>
                    {comments.length > 0 ? (
                        comments.map((c, idx) => (
                            <Typography 
                                key={c._id || idx} 
                                variant="body2" 
                                sx={{ mt: 1 }}
                            >
                                {idx + 1}. {c.comment}
                            </Typography>
                        ))
                    ) : (
                        <Typography 
                            variant="caption" 
                            color="text.secondary"
                        >
                            No comments available.
                        </Typography>
                    )}
                </CardContent>
            </Collapse>

            {/* ── ACTIONS ── */}
            <CardActions disableSpacing sx={{ px: 2, pt: 0 }}>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={commentsExpanded ? 'Hide comments' : `Comments (${comments.length})`} arrow>
                        <IconButton
                            size="small"
                            onClick={() => setCommentsExpanded(p => !p)}
                            sx={{
                                transform: commentsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                            }}
                        >
                            <ExpandMore fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardActions>

            <DeleteConfirm
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={executeDelete}
                deleteType={deleteType}
            />
        </Card>
    );
};

export default RequestCard;
